const APPSYNC_ENDPOINT = import.meta.env.VITE_APPSYNC_ENDPOINT || '';

interface GraphQLResponse<T = Record<string, unknown>> {
  data?: T;
  errors?: Array<{ message: string; locations?: unknown[]; path?: string[] }>;
}

export async function graphql<T = Record<string, unknown>>(
  idToken: string,
  query: string,
  variables?: Record<string, unknown>,
): Promise<T> {
  if (!APPSYNC_ENDPOINT) {
    throw new Error('AppSync endpoint not configured');
  }

  const response = await fetch(APPSYNC_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`AppSync request failed (${response.status}): ${text}`);
  }

  const result: GraphQLResponse<T> = await response.json();

  if (result.errors && result.errors.length > 0) {
    throw new Error(result.errors[0].message);
  }

  if (!result.data) {
    throw new Error('No data returned from AppSync');
  }

  return result.data;
}

// GraphQL operations

const CREATE_SCAN_SESSION = `
  mutation CreateScanSession($input: CreateScanSessionInput!) {
    createScanSession(input: $input) {
      scanSessionId
      uploadTargets {
        presignedUrl
        s3Key
        imageIndex
      }
      manifestUploadTarget {
        presignedUrl
        s3Key
        imageIndex
      }
    }
  }
`;

const GET_SCAN = `
  query GetScan($scanSessionId: ID!) {
    getScan(scanSessionId: $scanSessionId) {
      session {
        scanSessionId
        userId
        status
        expectedImageCount
        receivedImageCount
        categoryHint
        resolvedCategory
        createdAt
        updatedAt
      }
      latestFast
      latestDeep
      images {
        imageId
        captureOrder
        url
        thumbnailUrl
      }
    }
  }
`;

const LIST_SCAN_MESSAGES = `
  query ListScanMessages($scanSessionId: ID!, $afterSeq: Int) {
    listScanMessages(scanSessionId: $scanSessionId, afterSeq: $afterSeq) {
      messages {
        scanSessionId
        seq
        messageId
        role
        type
        payloadVersion
        payload
        createdAt
      }
      lastSeq
    }
  }
`;

// Types

export interface UploadTarget {
  presignedUrl: string;
  s3Key: string;
  imageIndex: number;
}

export interface CreateScanSessionResult {
  scanSessionId: string;
  uploadTargets: UploadTarget[];
  manifestUploadTarget: UploadTarget;
}

export interface ScanSessionInfo {
  scanSessionId: string;
  userId: string;
  status: string;
  expectedImageCount: number;
  receivedImageCount: number;
  categoryHint?: string;
  resolvedCategory?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScanMessage {
  scanSessionId: string;
  seq: number;
  messageId: string;
  role: string;
  type: string;
  payloadVersion: number;
  payload: string;
  createdAt: string;
}

export interface ScanImage {
  imageId: string;
  captureOrder: number;
  url: string;
  thumbnailUrl: string;
}

export interface GetScanResult {
  session: ScanSessionInfo;
  latestFast?: string;
  latestDeep?: string;
  images: ScanImage[];
}

export interface ListScanMessagesResult {
  messages: ScanMessage[];
  lastSeq?: number;
}

// Service functions

export async function createScanSession(
  idToken: string,
  expectedImageCount: number,
  categoryHint?: string,
): Promise<CreateScanSessionResult> {
  const data = await graphql<{ createScanSession: CreateScanSessionResult }>(
    idToken,
    CREATE_SCAN_SESSION,
    { input: { expectedImageCount, categoryHint: categoryHint || undefined } },
  );
  return data.createScanSession;
}

export async function getScan(
  idToken: string,
  scanSessionId: string,
): Promise<GetScanResult> {
  const data = await graphql<{ getScan: GetScanResult }>(
    idToken,
    GET_SCAN,
    { scanSessionId },
  );
  return data.getScan;
}

export async function listScanMessages(
  idToken: string,
  scanSessionId: string,
  afterSeq?: number,
): Promise<ListScanMessagesResult> {
  const data = await graphql<{ listScanMessages: ListScanMessagesResult }>(
    idToken,
    LIST_SCAN_MESSAGES,
    { scanSessionId, afterSeq: afterSeq ?? 0 },
  );
  return data.listScanMessages;
}

export async function uploadImage(
  presignedUrl: string,
  file: File,
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': 'image/jpeg' },
  });
  if (!response.ok) {
    throw new Error(`Failed to upload image: ${response.status}`);
  }
}

export async function uploadManifest(
  presignedUrl: string,
  manifest: {
    scanSessionId: string;
    expectedImageCount: number;
    images: Array<{ s3Key: string; captureOrder: number; contentType: string }>;
    clientHints?: { categoryHint?: string };
  },
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: JSON.stringify(manifest),
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Failed to upload manifest: ${response.status}`);
  }
}
